import React from 'react';
import { Modal } from 'react-bootstrap';
import { ArrowSync24Filled, Warning48Filled } from '@fluentui/react-icons';
import useAppUpdateCheck from '../../../hooks/useAppUpdateCheck';
import styles from './AppUpdateModal.module.css';

const isMacPlatform = () => {
    if (typeof navigator === 'undefined') return false;
    const platform = navigator.userAgentData?.platform || navigator.platform || '';
    const ua = navigator.userAgent || '';
    return /Mac|iPhone|iPad|iPod/i.test(platform) || /Mac OS X/i.test(ua);
};

const Key = ({ children }) => <kbd className={styles.key}>{children}</kbd>;

const Shortcut = ({ keys }) => (
    <span className={styles.shortcut}>
        {keys.map((key, index) => (
            <React.Fragment key={`${key}-${index}`}>
                {index > 0 ? <span className={styles.keyPlus}>+</span> : null}
                <Key>{key}</Key>
            </React.Fragment>
        ))}
    </span>
);

/**
 * Soft update prompt when a new frontend or backend deploy is detected.
 * Dismissible; leaves a reminder banner until the user hard-refreshes.
 */
const AppUpdateModal = () => {
    const { showModal, showBanner, dismissModal, openModal, refreshNow } = useAppUpdateCheck();
    const isMac = React.useMemo(() => isMacPlatform(), []);

    return (
        <>
            {showBanner && !showModal ? (
                <div className={styles.banner} role="status">
                    <ArrowSync24Filled />
                    <p className={styles.bannerText}>A new version of Lens is available.</p>
                    <button type="button" className={styles.bannerRefresh} onClick={refreshNow}>
                        Refresh
                    </button>
                    <button type="button" className={styles.bannerLater} onClick={openModal}>
                        Details
                    </button>
                </div>
            ) : null}

            <Modal
                show={showModal}
                onHide={dismissModal}
                backdrop="static"
                keyboard={false}
                centered
                backdropClassName={styles.backdrop}
                className={styles.modalRoot}
                dialogClassName={styles.dialog}>
                <Modal.Body className={styles.body}>
                    <div className="mb-3 text-center">
                        <Warning48Filled color="var(--Brand-Primary)" />
                    </div>
                    <div>
                        <h5 className={styles.title}>Update Available</h5>
                        <p className={styles.message}>
                            A new version of Lens is available. Unsaved work may be affected — save your
                            changes, then hard refresh to load the latest version, or use the button below.
                        </p>
                        <div className={styles.shortcuts}>
                            <div className={styles.shortcutRow}>
                                <Shortcut keys={isMac ? ['⌘', 'Shift', 'R'] : ['Ctrl', 'Shift', 'R']} />
                            </div>
                        </div>
                    </div>
                    <br />
                    <div className={styles.actions}>
                        <button type="button" className={styles.primaryBtn} onClick={refreshNow}>
                            Refresh now
                        </button>
                        <button type="button" className={styles.secondaryBtn} onClick={dismissModal}>
                            Later
                        </button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default AppUpdateModal;
